"use server";

import { revalidatePath } from "next/cache";
import { cookies } from "next/headers";
import { api, ConduitError, type RequirementsResponse, type Originator } from "@/lib/conduit";
import { payoutApprovals, team, savedRecipients, senderInfoSubmissions } from "@/lib/db";
import { getActingUser, canInitiate, canApprove } from "@/lib/acting-user";
import { screenInvoice } from "@/lib/invoice-screener";
import type { PayoutInitialValues, PayoutPrefill } from "@/app/payouts/payout-form";

/* ---------- Active customer (demo view switcher) ---------- */

export async function setActiveCustomerAction(id: string): Promise<{ ok: true }> {
  (await cookies()).set("active_customer", id, { path: "/", sameSite: "lax" });
  revalidatePath("/", "layout");
  return { ok: true };
}

/* ---------- Acting user (in-app identity, no SSO yet) ---------- */

export async function setActingUserAction(email: string): Promise<{ ok: true }> {
  (await cookies()).set("acting_as", email.trim().toLowerCase(), { path: "/", sameSite: "lax" });
  revalidatePath("/", "layout");
  return { ok: true };
}

export async function addTeamMemberAction(input: {
  customerId: string;
  name?: string;
  email: string;
  role: "initiator" | "approver" | "admin";
}): Promise<ActionResult> {
  try {
    if (!input.email.includes("@")) return { ok: false, error: "A valid email is required." };
    const m = team.add({ customer_id: input.customerId, name: input.name, email: input.email, role: input.role });
    revalidatePath("/", "layout");
    return { ok: true, data: m };
  } catch (e) {
    const msg = e instanceof Error && e.message.includes("UNIQUE") ? "That email is already on the team." : undefined;
    return msg ? { ok: false, error: msg } : fail(e);
  }
}

export async function removeTeamMemberAction(id: number): Promise<ActionResult> {
  team.remove(id);
  revalidatePath("/", "layout");
  return { ok: true, data: null };
}

export type ActionResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; error: string; type?: string; fieldErrors?: Array<{ pointer?: string; detail?: string }> };

function fail(e: unknown): ActionResult<never> {
  if (e instanceof ConduitError) {
    return {
      ok: false,
      error: e.resolution ? `${e.detail} — ${e.resolution}` : e.detail,
      type: e.type,
      fieldErrors: e.errors?.map((x) => ({ pointer: x.pointer ?? x.field, detail: x.detail ?? x.message })),
    };
  }
  return { ok: false, error: e instanceof Error ? e.message : "Unexpected error" };
}

/* ---------- Documents ---------- */

export async function uploadDocumentAction(formData: FormData): Promise<ActionResult<{ id: string; fileName?: string }>> {
  try {
    const doc = await api.uploadDocument(formData);
    return { ok: true, data: { id: doc.id, fileName: doc.fileName ?? doc.name } };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Onboarding ---------- */

export async function onboardingRequirementsAction(country: string): Promise<ActionResult<RequirementsResponse>> {
  try {
    const reqs = await api.onboardingRequirements(country);
    return { ok: true, data: reqs };
  } catch (e) {
    return fail(e);
  }
}

export async function submitOnboardingAction(payload: unknown): Promise<ActionResult<{ id: string; status: string }>> {
  try {
    const app = await api.submitOnboarding(payload);
    revalidatePath("/onboarding");
    revalidatePath("/customers");
    return { ok: true, data: { id: app.id, status: app.status } };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Features / provisioning ---------- */

export async function requestFeatureAction(
  customerId: string,
  body: { type: "virtual_account" | "crypto_wallet"; asset?: string },
): Promise<ActionResult> {
  try {
    const app = await api.requestFeature(customerId, body);
    revalidatePath("/account");
    return { ok: true, data: app };
  } catch (e) {
    return fail(e);
  }
}

export async function createWalletAction(customerId: string, chain: string): Promise<ActionResult> {
  try {
    const w = await api.createWallet(customerId, { chain });
    revalidatePath("/account");
    return { ok: true, data: w };
  } catch (e) {
    return fail(e);
  }
}

export async function claimNonCustodialAction(
  customerId: string,
  body: {
    roster: Array<{ email: string; name?: string; role: "admin" | "signer"; credentialType: "passkey" }>;
    signingThreshold: number;
    /** Omit to auto-provision every supported chain (Conduit's recommendation — a partial set can't be widened without re-auth). */
    chains?: string[];
  },
): Promise<ActionResult> {
  try {
    const r = await api.claimNonCustodial(customerId, body);
    revalidatePath("/account");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Orders (conversions) ---------- */

export async function createOrderAction(body: unknown): Promise<ActionResult> {
  try {
    const order = await api.createOrder(body);
    revalidatePath("/convert");
    return { ok: true, data: order };
  } catch (e) {
    return fail(e);
  }
}

export async function executeOrderAction(id: string): Promise<ActionResult> {
  try {
    const order = await api.executeOrder(id);
    revalidatePath("/convert");
    revalidatePath("/transactions");
    return { ok: true, data: order };
  } catch (e) {
    return fail(e);
  }
}

export async function cancelOrderAction(id: string): Promise<ActionResult> {
  try {
    const order = await api.cancelOrder(id);
    revalidatePath("/convert");
    return { ok: true, data: order };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Payouts ---------- */

export type PayoutRequirements = {
  rail?: string;
  fields?: Array<{
    name: string;
    type?: string;
    required?: boolean;
    enum?: string[];
    validator?: string;
    minLength?: number;
    maxLength?: number;
  }>;
  documentation?: { required?: boolean; reason?: string; acceptedDocumentTypes?: string[] } | null;
  whitelist?: { required?: boolean } | null;
};

export async function payoutRequirementsAction(
  query: Record<string, string | undefined>,
): Promise<ActionResult<PayoutRequirements>> {
  try {
    const r = (await api.payoutRequirements(query)) as PayoutRequirements;
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

export async function createPayoutAction(body: unknown): Promise<ActionResult<{ id: string }>> {
  try {
    const txn = await api.createPayout(body);
    revalidatePath("/payouts");
    revalidatePath("/transactions");
    return { ok: true, data: { id: txn.id } };
  } catch (e) {
    return fail(e);
  }
}

export async function cancelPayoutAction(id: string): Promise<ActionResult> {
  try {
    const txn = await api.cancelPayout(id);
    revalidatePath("/payouts");
    return { ok: true, data: txn };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Invoice screener ---------- */

export type InvoiceScreenerResult = {
  initial: PayoutInitialValues;
  prefill: PayoutPrefill;
};

const SCREENABLE_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/gif", "image/webp"]);

/** Reads an uploaded invoice and turns it into payout-form prefill data, attaching the invoice itself as the supporting document. */
export async function screenInvoiceAction(formData: FormData): Promise<ActionResult<InvoiceScreenerResult>> {
  try {
    const file = formData.get("file");
    if (!(file instanceof File) || file.size === 0) return { ok: false, error: "Choose an invoice file first." };
    if (!SCREENABLE_TYPES.has(file.type)) {
      return { ok: false, error: `Unsupported file type (${file.type || "unknown"}) — upload a PDF, JPG, PNG, GIF or WEBP.` };
    }

    const bytes = Buffer.from(await file.arrayBuffer());

    const [extracted, doc] = await Promise.all([
      screenInvoice(bytes, file.type),
      (async () => {
        const uploadForm = new FormData();
        uploadForm.set("file", file);
        uploadForm.set("purpose", "transaction_support");
        uploadForm.set("name", file.name);
        return api.uploadDocument(uploadForm);
      })().catch(() => null),
    ]);

    const destCountry = extracted.iban
      ? extracted.iban.slice(0, 2).toUpperCase()
      : (extracted.country?.toUpperCase() || "US");

    const values: Record<string, string> = {};
    if (extracted.vendorLegalName) values["destination.recipient.legalName"] = extracted.vendorLegalName;
    if (extracted.bankName) values["destination.recipient.bankName"] = extracted.bankName;
    if (extracted.accountNumber) values["destination.recipient.accountNumber"] = extracted.accountNumber;
    if (extracted.routingNumber) values["destination.recipient.routingNumber"] = extracted.routingNumber;
    if (extracted.iban) values["destination.recipient.iban"] = extracted.iban;
    if (extracted.bic) values["destination.recipient.bic"] = extracted.bic;
    if (extracted.invoiceNumber) values["destination.remittance.reference"] = extracted.invoiceNumber;
    if (extracted.addressLine1) values["destination.recipient.postalAddress.addressLine1"] = extracted.addressLine1;
    if (extracted.addressLine2) values["destination.recipient.postalAddress.addressLine2"] = extracted.addressLine2;
    if (extracted.city) values["destination.recipient.postalAddress.city"] = extracted.city;
    if (extracted.state) values["destination.recipient.postalAddress.state"] = extracted.state;
    if (extracted.postalCode) values["destination.recipient.postalAddress.postalCode"] = extracted.postalCode;
    if (extracted.country) values["destination.recipient.postalAddress.country"] = extracted.country.toUpperCase();

    const warnings = [...(extracted.warnings ?? [])];
    if (!extracted.vendorLegalName) warnings.push("Couldn't find a vendor legal name — enter it manually.");
    if (!extracted.amount) warnings.push("Couldn't find an amount — enter it manually.");
    if (!extracted.accountNumber && !extracted.iban) warnings.push("Couldn't find bank account details — enter them manually.");

    return {
      ok: true,
      data: {
        initial: {
          kind: "fiat",
          code: (extracted.currency || "USD").toUpperCase(),
          amount: extracted.amount ?? "",
          lockSide: "destination",
        },
        prefill: {
          destCountry,
          recipientType: "business",
          purpose: "payment_for_goods_or_services",
          values,
          docs: doc ? [{ id: doc.id, fileName: doc.fileName ?? doc.name ?? file.name }] : [],
          warnings,
        },
      },
    };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Permissions (signers & quorum) ---------- */

export async function addSignerAction(
  customerId: string,
  body: { email: string; name?: string; role: "admin" | "signer"; credentialType: "passkey" | "api_key"; publicKey?: string },
): Promise<ActionResult> {
  try {
    const s = await api.addSigner(customerId, body);
    revalidatePath("/permissions");
    return { ok: true, data: s };
  } catch (e) {
    return fail(e);
  }
}

export async function removeSignerAction(customerId: string, signerId: string): Promise<ActionResult> {
  try {
    await api.removeSigner(customerId, signerId);
    revalidatePath("/permissions");
    return { ok: true, data: null };
  } catch (e) {
    return fail(e);
  }
}

export async function reissueSignerInviteAction(
  customerId: string,
  signerId: string,
): Promise<ActionResult<{ verificationUrl: string; expiresAt?: string }>> {
  try {
    const s = await api.reissueSignerInvite(customerId, signerId);
    return { ok: true, data: { verificationUrl: s.verificationUrl, expiresAt: s.expiresAt } };
  } catch (e) {
    return fail(e);
  }
}

export async function promoteSignerAction(customerId: string, signerId: string): Promise<ActionResult> {
  try {
    const r = await api.promoteSigner(customerId, signerId);
    revalidatePath("/permissions");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

export async function demoteSignerAction(customerId: string, signerId: string): Promise<ActionResult> {
  try {
    const r = await api.demoteSigner(customerId, signerId);
    revalidatePath("/permissions");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

export async function setQuorumAction(customerId: string, threshold: number): Promise<ActionResult> {
  try {
    const r = await api.setQuorum(customerId, threshold);
    revalidatePath("/permissions");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Payout approvals (maker-checker) ----------
   Conduit has no native approval layer for fiat payouts, so the app enforces
   one: an initiator submits, and only a DIFFERENT person can approve. Approval
   is when the Conduit API is actually called. */

export async function submitPayoutForApprovalAction(input: {
  customerId: string;
  kind: "payout" | "order";
  body: unknown;
  summary: string;
}): Promise<ActionResult<{ id: number }>> {
  try {
    // Identity comes from the server-side acting-user cookie, never the client.
    const actor = await getActingUser(input.customerId);
    if (!actor) return { ok: false, error: "Select who you're acting as (top-left) — you must be on this customer's team." };
    if (!canInitiate(actor)) return { ok: false, error: `${actor.email} has the '${actor.role}' role and cannot initiate payouts.` };
    const row = payoutApprovals.create({
      customer_id: input.customerId,
      kind: input.kind,
      body_json: JSON.stringify(input.body),
      summary: input.summary,
      initiated_by: actor.email,
    });
    revalidatePath("/payouts");
    return { ok: true, data: { id: row.id } };
  } catch (e) {
    return fail(e);
  }
}

export async function decidePayoutApprovalAction(
  id: number,
  decision: "approve" | "reject",
): Promise<ActionResult<{ status: string; ref?: string }>> {
  const row = payoutApprovals.get(id);
  if (!row) return { ok: false, error: "Approval request not found." };
  if (row.status !== "pending") return { ok: false, error: `Already ${row.status}.` };

  const actor = await getActingUser(row.customer_id);
  if (!actor) return { ok: false, error: "Select who you're acting as (top-left) — you must be on this customer's team." };
  if (!canApprove(actor)) return { ok: false, error: `${actor.email} has the '${actor.role}' role and cannot approve payouts.` };
  const who = actor.email;
  // Four-eyes rule: the initiator can never approve their own payout.
  if (who === row.initiated_by) {
    return { ok: false, error: "Separation of duties: the initiator cannot approve their own payout." };
  }

  if (decision === "reject") {
    payoutApprovals.decide(id, who, "rejected");
    revalidatePath("/payouts");
    return { ok: true, data: { status: "rejected" } };
  }

  try {
    const body = JSON.parse(row.body_json);
    let ref: string;
    if (row.kind === "order") {
      // cross-currency: create the conversion order and execute at approval
      // time (the rate is quoted now, not at initiation)
      const order = await api.createOrder(body);
      if ((body as { autoExecute?: boolean }).autoExecute) {
        // Unfunded order: a manual execute would 422 INSUFFICIENT_FUNDS
        // immediately — Conduit's sweep executes it once the source balance
        // covers totalDebit, so creation is the whole job here.
        ref = order.id;
      } else {
        const executed = await api.executeOrder(order.id);
        ref = executed.id ?? order.id;
      }
    } else {
      const txn = await api.createPayout(body);
      ref = txn.id;
    }
    payoutApprovals.decide(id, who, "approved", ref);
    revalidatePath("/payouts");
    revalidatePath("/transactions");
    return { ok: true, data: { status: "approved", ref } };
  } catch (e) {
    // Keep the field-level details — "VALIDATION_ERROR: Validation failed"
    // alone gives the approver nothing to act on.
    const details =
      e instanceof ConduitError && e.errors?.length
        ? ` — ${e.errors.map((x) => `${x.pointer ?? x.field ?? ""}: ${x.detail ?? x.message ?? ""}`.trim()).join("; ")}`
        : "";
    const msg = e instanceof ConduitError ? `${e.type}: ${e.detail}${details}` : e instanceof Error ? e.message : "failed";
    payoutApprovals.decide(id, who, "failed", undefined, msg);
    revalidatePath("/payouts");
    return { ok: false, error: msg };
  }
}

/* ---------- Registered sender addresses (crypto deposit whitelist) ---------- */

export async function registerWalletAddressAction(customerId: string, body: unknown): Promise<ActionResult> {
  try {
    const r = await api.registerWalletAddress(customerId, body);
    revalidatePath("/account/addresses");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

export async function revokeRegisteredAddressAction(id: string): Promise<ActionResult> {
  try {
    await api.revokeRegisteredAddress(id);
    revalidatePath("/account/addresses");
    return { ok: true, data: null };
  } catch (e) {
    return fail(e);
  }
}

export async function provideSenderInformationAction(
  transactionId: string,
  body: { originator: Originator; register?: boolean; label?: string },
): Promise<ActionResult> {
  try {
    const r = await api.provideSenderInformation(transactionId, body);
    // Conduit 202s and then exposes nothing about the submission on the
    // transaction — log it locally so the UI can say "already submitted"
    // instead of re-offering a blank form forever.
    senderInfoSubmissions.record(
      transactionId,
      body.originator.entityType === "business"
        ? body.originator.legalName
        : `${body.originator.firstName} ${body.originator.lastName}`,
    );
    revalidatePath("/transactions");
    revalidatePath(`/transactions/${transactionId}`);
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Whitelist recipients (intercompany payouts) ---------- */

export async function registerWhitelistRecipientAction(
  customerId: string,
  body: unknown,
): Promise<ActionResult> {
  try {
    const r = await api.registerWhitelistRecipient(customerId, body);
    revalidatePath("/payouts/whitelist");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

export async function revokeWhitelistRecipientAction(customerId: string, id: string): Promise<ActionResult> {
  try {
    await api.revokeWhitelistRecipient(customerId, id);
    revalidatePath("/payouts/whitelist");
    return { ok: true, data: null };
  } catch (e) {
    return fail(e);
  }
}

/* ---------- Saved recipients (local address book — Conduit has no
   general recipient registry outside the intercompany whitelist) ---------- */

export async function createSavedRecipientAction(input: {
  customerId: string;
  label: string;
  kind: "fiat" | "crypto";
  destCountry?: string;
  recipientType?: string;
  chain?: string;
  address?: string;
  custody?: string;
  values?: Record<string, string>;
}): Promise<ActionResult> {
  try {
    if (!input.label.trim()) return { ok: false, error: "Give this recipient a name." };
    const r = savedRecipients.create({
      customer_id: input.customerId,
      label: input.label.trim(),
      kind: input.kind,
      dest_country: input.destCountry,
      recipient_type: input.recipientType,
      chain: input.chain,
      address: input.address,
      custody: input.custody,
      values: input.values,
    });
    revalidatePath("/payouts");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}

export async function removeSavedRecipientAction(id: number): Promise<ActionResult> {
  savedRecipients.remove(id);
  revalidatePath("/payouts");
  return { ok: true, data: null };
}

/* ---------- RFIs ---------- */

export async function respondRfiAction(
  id: string,
  customerId: string,
  body: { message: string; documentIds?: string[] },
): Promise<ActionResult> {
  // Conduit requires submittedBy.email — an API key alone has no user
  // identity. Use the server-side acting-user cookie, never a client claim.
  const actor = await getActingUser(customerId);
  if (!actor) {
    return { ok: false, error: "Select who you're acting as (top-left) before responding — Conduit records who answered." };
  }
  try {
    const r = await api.respondRfi(id, { ...body, submittedBy: { email: actor.email } });
    revalidatePath("/rfis");
    return { ok: true, data: r };
  } catch (e) {
    return fail(e);
  }
}
